/**
 * Hook: GET /api/t/{slug}/search — the command palette's server-side groups.
 *
 * Cache key: `["global-search", tenantSlug, query]`.
 *
 * STALE RESPONSES CANNOT WIN — two independent mechanisms:
 *
 * 1. The query key includes the term. React Query stores each term's result in
 *    its own cache entry and the component only ever reads the entry for the
 *    term it is currently rendering. A slow request for "an" resolving after a
 *    fast one for "anna" writes to the "an" entry, which nothing is reading.
 *    This is structural — there is no ordering bug to get wrong.
 * 2. `signal` is forwarded to axios, so React Query aborts the previous request
 *    when the key changes. Belt to (1)'s braces: it stops the wasted round trip
 *    rather than merely ignoring it.
 *
 * DEBOUNCE lives in the caller, not here. The palette owns the input, so it
 * owns the ~200 ms settle before a term is promoted to a query key — passing an
 * undebounced term to this hook would mint one cache entry per keystroke.
 *
 * MINIMUM LENGTH mirrors the backend: `q` is validated `2..100` server-side and
 * anything shorter is a 422, so this hook stays disabled below 2 characters
 * rather than firing a request whose only possible outcome is an error.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

/** Backend `SearchCandidateOut` — a display row, not the full candidate. */
interface SearchCandidateHit {
  id: string;
  name: string;
  email: string | null;
}

/** Backend `SearchJdOut`. `name` (not `title`) matches the rest of the API. */
interface SearchJdHit {
  id: string;
  name: string;
  status: string;
}

export interface GlobalSearchResults {
  candidates: SearchCandidateHit[];
  jds: SearchJdHit[];
}

/**
 * Shortest query the endpoint accepts. Kept in lockstep with the backend's
 * `MIN_QUERY_LEN` (`app/services/search_terms.py`).
 */
export const SEARCH_MIN_QUERY_LENGTH = 2;

/** Per-group cap the endpoint enforces (`MAX_PER_GROUP`). */
export const SEARCH_GROUP_LIMIT = 10;

/**
 * Called only from a MOUNTED (i.e. open) palette, so there is no `enabled`
 * prop for open-state: the caller unmounts instead. The only gates here are
 * the ones intrinsic to the request — a resolvable tenant slug and a term long
 * enough for the endpoint to accept.
 *
 * @param query the DEBOUNCED search term; the hook stays idle below the minimum
 */
export function useGlobalSearch(query: string): UseQueryResult<GlobalSearchResults, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const term = query.trim();
  const longEnough = term.length >= SEARCH_MIN_QUERY_LENGTH;

  return useQuery<GlobalSearchResults, ApiError>({
    queryKey: ["global-search", tenantSlug, term],
    queryFn: async ({ signal }) => {
      const r = await apiClient.get<GlobalSearchResults>(`/api/t/${tenantSlug}/search`, {
        params: { q: term, limit: SEARCH_GROUP_LIMIT },
        signal,
      });
      return r.data;
    },
    enabled: longEnough && !!tenantSlug,
    // A candidate or req created seconds ago is worth showing, but re-running
    // the same term inside a single palette session is not. Short window: the
    // palette is open for seconds, so this mostly de-dupes backspace-retype.
    staleTime: 15_000,
    // Never retry. A failed search should surface its error row immediately;
    // silently retrying leaves the palette spinning while the user waits for a
    // result that is not coming.
    retry: false,
  });
}
